"use client"

import { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import styles from "./Select.module.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons"

const PaginationFooter = ({ meta, onPrev, onNext, paginationLoading, activeBtn }) => {
  if (!meta) return null

  const get = (kCandidates) => {
    for (const k of kCandidates) if (k in meta) return meta[k]
    return null
  }

  const current = get(["current_page", "page", "current", "page_number", "page_no"]) ?? "-"
  const total = get(["total_pages", "total_pages_count", "totalPages"]) ?? "-"
  const prev = get(["previous", "prev", "previous_page", "prev_page", "previous_url"])
  const next = get(["next", "next_page", "next_url"])

  return (
    <div className={styles.dropdown_footer}>
      <button
        type="button"
        className={`${styles.paging_btn} ${activeBtn === "prev" ? styles.activePaging : ""}`}
        onClick={() => onPrev && onPrev(prev)}
        disabled={!prev || paginationLoading}
        aria-label="Previous"
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>

      <div className={styles.paging_info}>
        Page <b>{current}</b> of <b>{total}</b>
      </div>

      <button
        type="button"
        className={`${styles.paging_btn} ${activeBtn === "next" ? styles.activePaging : ""}`}
        onClick={() => onNext && onNext(next)}
        disabled={!next || paginationLoading}
        aria-label="Next"
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  )
}

const Select = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  error = false,
  className = "",
  size = "medium",
  width = "200px",
  height = "35px",
  searchable = true,
  renderOption,
  asyncFetch = null,
  debounceDelay = 300,
  minSearchLength = 0,
  returnFullOptionOnSelect = false,
  pagination = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [dropdownStyles, setDropdownStyles] = useState({})
  const [internalOptions, setInternalOptions] = useState(options || [])
  const [loading, setLoading] = useState(false)
  const [paginationLoading, setPaginationLoading] = useState(false)
  const [meta, setMeta] = useState(null)
  const [activePagingBtn, setActivePagingBtn] = useState(null)

  const selectRef = useRef(null)
  const dropdownRef = useRef(null)
  const debounceTimerRef = useRef(null)
  const lastFetchIdRef = useRef(0)

  // keep internalOptions synced when options prop changes, unless asyncFetch is used
  useEffect(() => {
    if (!asyncFetch) setInternalOptions(options || [])
    else {
      // if asyncFetch is used but initial options prop is present, use it
      if (options && options.length > 0) setInternalOptions(options)
    }
  }, [options, asyncFetch])

  useEffect(() => {
  // treat undefined / null / empty-string as no selection so placeholder shows
  if (value === undefined || value === null || value === "") {
    setSelectedOption(null)
    return
  }

  const findIn = (list) => {
    if (!Array.isArray(list)) return null
    return list.find((opt) => String(opt?.value) === String(value))
  }

  const foundInternal = findIn(internalOptions)
  if (foundInternal) {
    setSelectedOption(foundInternal)
    return
  }

  const foundProp = findIn(options)
  if (foundProp) {
    setSelectedOption(foundProp)
    return
  }

  // fallback: if parent passed a value that isn't present in options, create fallback
  setSelectedOption({ label: String(value), value })
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [value, internalOptions, options])


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (isOpen && selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect()
        setDropdownStyles({
          position: "absolute",
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          zIndex: 9999,
        })
      }
    }

    if (isOpen) {
      updateDropdownPosition()
      window.addEventListener("scroll", updateDropdownPosition, true)
      window.addEventListener("resize", updateDropdownPosition)
    }

    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true)
      window.removeEventListener("resize", updateDropdownPosition)
    }
  }, [isOpen])

  // debounced async fetch
  useEffect(() => {
    if (!asyncFetch) return
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    if (!searchTerm || searchTerm.length < (minSearchLength || 0)) {
      // keep existing internalOptions if present (so default list still shows)
      // but clear meta/loader
      setMeta(null)
      setLoading(false)
      return
    }

    const currentFetchId = ++lastFetchIdRef.current
    setLoading(true)
    debounceTimerRef.current = setTimeout(() => {
      Promise.resolve()
        .then(() => asyncFetch(searchTerm, 1))
        .then((fetched) => {
          if (currentFetchId !== lastFetchIdRef.current) return
          if (Array.isArray(fetched)) {
            setInternalOptions(fetched)
            setMeta(null)
          } else if (fetched && typeof fetched === "object") {
            const results = fetched.results ?? fetched.data ?? fetched.data?.results ?? fetched
            const metaObj = fetched.meta ?? fetched.data?.meta ?? null
            setInternalOptions(Array.isArray(results) ? results : [])
            setMeta(metaObj)
          } else {
            setInternalOptions([])
            setMeta(null)
          }
        })
        .catch((e) => {
          if (currentFetchId !== lastFetchIdRef.current) return
          setInternalOptions([])
          setMeta(null)
          console.error("Select asyncFetch error:", e)
        })
        .finally(() => {
          if (currentFetchId === lastFetchIdRef.current) setLoading(false)
        })
    }, debounceDelay)

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, asyncFetch, debounceDelay, minSearchLength])

  const handleToggle = () => {
    if (!disabled) setIsOpen((prev) => !prev)
  }

  const handleSelect = (option) => {
    setSelectedOption(option)
    setSearchTerm("")
    setIsOpen(false)
    if (typeof onChange === "function") {
      onChange(returnFullOptionOnSelect ? option : option?.value)
    }
  }

  const filteredOptions = (internalOptions || []).filter((opt) =>
    (opt.label || "").toString().toLowerCase().includes(searchTerm.toString().toLowerCase())
  )

  const selectClasses = [styles.select, styles[size], isOpen && styles.open, disabled && styles.disabled, error && styles.error, className]
    .filter(Boolean)
    .join(" ")

  // When meta.next/meta.previous is a full URL, convert to relative path before passing to asyncFetch.
  const normalizeCandidateToPath = (candidate) => {
    try {
      if (!candidate) return null
      if (typeof candidate === "string" && candidate.startsWith("http")) {
        const u = new URL(candidate)
        return `${u.pathname}${u.search}`
      }
      // if already a relative path like "/inventory/..." return as-is
      if (typeof candidate === "string" && candidate.startsWith("/")) return candidate
      return candidate
    } catch (e) {
      return candidate
    }
  }

  // handle pagination clicks
  const handlePageFetch = (which) => {
    if (!asyncFetch || !meta) return

    const get = (kCandidates) => {
      for (const k of kCandidates) if (k in meta) return meta[k]
      return null
    }

    const rawCandidate = which === "prev" ? get(["previous", "prev", "previous_page", "prev_page", "previous_url"]) : get(["next", "next_page", "next_url"])
    if (!rawCandidate) return

    // normalize to either page number or relative path
    const candidate = normalizeCandidateToPath(rawCandidate)

    setActivePagingBtn(which)
    setPaginationLoading(true)
    const currentFetchId = ++lastFetchIdRef.current

    Promise.resolve()
      .then(() => asyncFetch(searchTerm, candidate))
      .then((fetched) => {
        if (currentFetchId !== lastFetchIdRef.current) return
        if (Array.isArray(fetched)) {
          setInternalOptions(fetched)
          setMeta(null)
        } else if (fetched && typeof fetched === "object") {
          const results = fetched.results ?? fetched.data ?? fetched.data?.results ?? fetched
          const metaObj = fetched.meta ?? fetched.data?.meta ?? null
          setInternalOptions(Array.isArray(results) ? results : [])
          setMeta(metaObj)
        } else {
          setInternalOptions([])
          setMeta(null)
        }
      })
      .catch((e) => {
        if (currentFetchId !== lastFetchIdRef.current) return
        console.error("Select pagination fetch error:", e)
      })
      .finally(() => {
        if (currentFetchId === lastFetchIdRef.current) {
          setPaginationLoading(false)
          setTimeout(() => setActivePagingBtn(null), 400)
        }
      })
  }

  return (
    <>
      <div className={styles.container} ref={selectRef} style={{ width }}>
        <div className={selectClasses} onClick={handleToggle} style={{ height }} {...props}>
          <span className={styles.value}>{selectedOption ? selectedOption.label : placeholder}</span>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className={styles.icon} />
        </div>
      </div>

      {isOpen &&
        createPortal(
          <div ref={dropdownRef} className={styles.dropdown} style={{ ...dropdownStyles, maxHeight: "320px" }}>
            {searchable && (
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={styles.search} placeholder="Search..." />
            )}

            <div className={styles.options}>
              {loading ? (
                <div className={styles.no_option}>Loading...</div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div key={option.value} className={`${styles.option} ${selectedOption?.value === option.value ? styles.selected : ""}`} onClick={() => handleSelect(option)}>
                    {renderOption ? renderOption(option) : option.label}
                  </div>
                ))
              ) : (
                <div className={styles.no_option}>No options found</div>
              )}
            </div>

            {pagination && meta && (
              <PaginationFooter meta={meta} onPrev={() => handlePageFetch("prev")} onNext={() => handlePageFetch("next")} paginationLoading={paginationLoading} activeBtn={activePagingBtn} />
            )}
          </div>,
          document.body
        )}
    </>
  )
}

export default Select
